import { useCallback, useEffect, useRef, useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import {
  EXTRAS,
  MENU_ITEMS,
  buildBill,
  componentLabel,
  formatMoney,
  getExtra,
  getMenuItem,
  type ActionEnvelope,
  type ChefTray,
  type GameAction,
  type MenuCategory,
  type MenuItem,
  type OrderSelection,
  type Role,
  type RoomView,
  type ServerMessage
} from "@table-for-two/shared";

type HomeMode = "home" | "create" | "join";
type ConnectionState = "connecting" | "online" | "offline";

const CATEGORY_LABELS: Record<MenuCategory, string> = {
  main: "Main meals",
  side: "Sides",
  drink: "Drinks",
  dessert: "Desserts"
};

const CATEGORY_ICONS: Record<MenuCategory, string> = {
  main: "🍽️",
  side: "🥗",
  drink: "🥤",
  dessert: "🍨"
};

export function App() {
  const [mode, setMode] = useState<HomeMode>("home");
  const [sessionCode, setSessionCode] = useState<string | null>(null);
  const [view, setView] = useState<RoomView | null>(null);
  const [connection, setConnection] = useState<ConnectionState>("offline");
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(true);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const savedCode = localStorage.getItem("restaurant-code");
    if (!savedCode) {
      setRestoring(false);
      return;
    }
    void fetch(`/api/restaurants/${savedCode}/state`, { credentials: "include", cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) throw new Error("The saved restaurant is no longer available.");
        const payload = await readApiPayload(response);
        setSessionCode(payload.code);
        setView(payload.view);
      })
      .catch(() => localStorage.removeItem("restaurant-code"))
      .finally(() => setRestoring(false));
  }, []);

  useEffect(() => {
    if (!sessionCode) return;
    let cancelled = false;
    let retryTimer: number | undefined;
    let retryDelay = 800;

    const connect = () => {
      if (cancelled) return;
      setConnection("connecting");
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(`${protocol}//${window.location.host}/api/restaurants/${sessionCode}/connect`);
      socketRef.current = socket;
      socket.addEventListener("open", () => {
        retryDelay = 800;
        setConnection("online");
      });
      socket.addEventListener("message", (event) => {
        const message = parseServerMessage(event.data);
        if (!message) return;
        if (message.type === "state") {
          setView(message.view);
          setNotice(null);
        } else {
          setNotice(message.message);
          if (message.view) setView(message.view);
        }
      });
      socket.addEventListener("close", () => {
        if (cancelled) return;
        setConnection("offline");
        retryTimer = window.setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 1.7, 5000);
      });
      socket.addEventListener("error", () => setConnection("offline"));
    };

    connect();
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [sessionCode]);

  const enterRestaurant = useCallback((code: string, nextView: RoomView) => {
    localStorage.setItem("restaurant-code", code);
    setSessionCode(code);
    setView(nextView);
    setNotice(null);
  }, []);

  const sendAction = useCallback((action: GameAction) => {
    if (!view || !socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      setNotice("The restaurant is reconnecting. Try again in a moment.");
      return;
    }
    const envelope: ActionEnvelope = {
      actionId: crypto.randomUUID(),
      expectedVersion: view.version,
      action
    };
    socketRef.current.send(JSON.stringify(envelope));
  }, [view]);

  const leaveRestaurant = () => {
    localStorage.removeItem("restaurant-code");
    socketRef.current?.close();
    setSessionCode(null);
    setView(null);
    setMode("home");
    setNotice(null);
  };

  if (restoring) return <LoadingScreen />;

  if (sessionCode && view) {
    return (
      <GameShell view={view} connection={connection} notice={notice} onDismissNotice={() => setNotice(null)} onLeave={leaveRestaurant}>
        <GameStage key={`${view.round}-${view.phase}`} view={view} onAction={sendAction} />
      </GameShell>
    );
  }

  return (
    <main className="landing-shell">
      <LandingHeader onHome={() => setMode("home")} />
      {notice && <Notice message={notice} onDismiss={() => setNotice(null)} />}
      {mode === "home" && <Home onCreate={() => setMode("create")} onJoin={() => setMode("join")} />}
      {mode === "create" && (
        <CreateRestaurant
          busy={busy}
          onBack={() => setMode("home")}
          onCreate={async (config) => {
            setBusy(true);
            setNotice(null);
            try {
              const response = await fetch("/api/restaurants", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(config)
              });
              const payload = await readApiPayload(response);
              enterRestaurant(payload.code, payload.view);
            } catch (error) {
              setNotice(error instanceof Error ? error.message : "The restaurant could not be created.");
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
      {mode === "join" && (
        <JoinRestaurant
          busy={busy}
          onBack={() => setMode("home")}
          onJoin={async (code) => {
            setBusy(true);
            setNotice(null);
            try {
              const response = await fetch(`/api/restaurants/${code}/join`, { method: "POST", credentials: "include" });
              const payload = await readApiPayload(response);
              enterRestaurant(payload.code, payload.view);
            } catch (error) {
              setNotice(error instanceof Error ? error.message : "The restaurant could not be joined.");
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </main>
  );
}

function LandingHeader({ onHome }: { onHome: () => void }) {
  return (
    <header className="landing-header">
      <button className="brand" onClick={onHome} aria-label="Table for Two home">
        <span className="brand-mark">2</span>
        <span><strong>TABLE</strong><small>FOR TWO</small></span>
      </button>
      <div className="header-note"><span>●</span> Two players · Two devices</div>
    </header>
  );
}

function Home({ onCreate, onJoin }: { onCreate: () => void; onJoin: () => void }) {
  return (
    <>
      <section className="hero">
        <div className="hero-copy">
          <p className="eyebrow">OPEN FOR FUN</p>
          <h1>Your restaurant.<br /><em>Your story.</em></h1>
          <p className="hero-intro">Take an order, cook it up, and see if the customer gets exactly what they asked for.</p>
          <div className="hero-actions">
            <button className="button button-primary button-large" onClick={onCreate}>Open a restaurant <span>→</span></button>
            <button className="button button-ghost button-large" onClick={onJoin}>Join with a code</button>
          </div>
        </div>
        <div className="hero-scene" aria-label="A customer and chef ready to play">
          <div className="sunburst" />
          <div className="speech speech-one">I’ll have the burger!</div>
          <div className="speech speech-two">Order up!</div>
          <div className="character customer-character"><span className="character-head">🙂</span><span className="character-body">CUSTOMER</span></div>
          <div className="table-scene"><span>🍔</span><span>🥤</span></div>
          <div className="character chef-character"><span className="chef-hat">♨</span><span className="character-head">😄</span><span className="character-body">CHEF</span></div>
        </div>
      </section>
      <section className="how-it-works">
        <div className="section-heading"><p className="eyebrow">HOW IT WORKS</p><h2>Three roles. One brilliant meal.</h2></div>
        <div className="steps">
          <article><span className="step-number">01</span><div className="step-icon coral">🗣️</div><h3>Take the order</h3><p>The customer chooses in secret and tells the waiter aloud.</p></article>
          <article><span className="step-number">02</span><div className="step-icon yellow">📝</div><h3>Write the ticket</h3><p>The waiter listens carefully and sends the order to the kitchen.</p></article>
          <article><span className="step-number">03</span><div className="step-icon teal">🍳</div><h3>Make & serve</h3><p>The chef builds the tray and the customer checks every item.</p></article>
        </div>
      </section>
    </>
  );
}

function CreateRestaurant({ busy, onBack, onCreate }: { busy: boolean; onBack: () => void; onCreate: (config: { name: string; menuItemIds: string[] }) => Promise<void> }) {
  const [name, setName] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => MENU_ITEMS.map((item) => item.id));
  const selectedItems = MENU_ITEMS.filter((item) => selectedIds.includes(item.id));
  const hasMain = selectedItems.some((item) => item.category === "main");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || selectedIds.length < 4 || !hasMain || busy) return;
    void onCreate({ name: name.trim(), menuItemIds: selectedIds });
  };

  return (
    <section className="setup-page">
      <button className="back-link" onClick={onBack}>← Back</button>
      <div className="setup-heading"><p className="eyebrow">RESTAURANT SETUP</p><h1>Build your menu</h1><p>Give your place a name, then choose what the kitchen can serve.</p></div>
      <form onSubmit={submit}>
        <label className="field-label" htmlFor="restaurant-name">Restaurant name</label>
        <input id="restaurant-name" className="text-input restaurant-name-input" value={name} maxLength={30} onChange={(event) => setName(event.target.value)} placeholder="e.g. The Happy Fork" autoFocus />
        <div className="menu-select-header"><h2>Choose your dishes</h2><span>{selectedIds.length} selected</span></div>
        {(["main", "side", "drink", "dessert"] as MenuCategory[]).map((category) => (
          <div className="menu-category" key={category}>
            <h3>{CATEGORY_ICONS[category]} {CATEGORY_LABELS[category]}</h3>
            <div className="menu-choice-grid">
              {MENU_ITEMS.filter((item) => item.category === category).map((item) => {
                const selected = selectedIds.includes(item.id);
                return (
                  <button
                    type="button"
                    aria-pressed={selected}
                    className={`menu-choice ${selected ? "selected" : ""}`}
                    key={item.id}
                    onClick={() => setSelectedIds((current) => selected ? current.filter((id) => id !== item.id) : [...current, item.id])}
                  >
                    <span className="menu-emoji">{item.emoji}</span><span><strong>{item.name}</strong><small>{formatMoney(item.pricePence)}</small></span><span className="choice-check">{selected ? "✓" : "+"}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div className="sticky-submit">
          <span>{!hasMain ? "Choose at least one main meal" : selectedIds.length < 4 ? "Choose at least four dishes" : "Your menu is ready"}</span>
          <button className="button button-primary button-large" disabled={busy || !name.trim() || selectedIds.length < 4 || !hasMain}>{busy ? "Opening…" : "Open restaurant →"}</button>
        </div>
      </form>
    </section>
  );
}

function JoinRestaurant({ busy, onBack, onJoin }: { busy: boolean; onBack: () => void; onJoin: (code: string) => Promise<void> }) {
  const [code, setCode] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (code.length === 6 && !busy) void onJoin(code);
  };
  return (
    <section className="join-page">
      <button className="back-link" onClick={onBack}>← Back</button>
      <div className="join-card">
        <div className="join-illustration">🍽️</div>
        <p className="eyebrow">YOUR TABLE IS WAITING</p>
        <h1>Join a restaurant</h1>
        <p>Ask the other player for the six-character code on their screen.</p>
        <form onSubmit={submit}>
          <label className="field-label" htmlFor="restaurant-code">Restaurant code</label>
          <input id="restaurant-code" className="code-input" value={code} onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^A-HJ-NP-Z2-9]/g, "").slice(0, 6))} placeholder="ABC234" autoComplete="off" autoCapitalize="characters" autoFocus />
          <button className="button button-primary button-large full-width" disabled={busy || code.length !== 6}>{busy ? "Finding your table…" : "Join restaurant →"}</button>
        </form>
      </div>
    </section>
  );
}

function GameShell({ view, connection, notice, onDismissNotice, onLeave, children }: { view: RoomView; connection: ConnectionState; notice: string | null; onDismissNotice: () => void; onLeave: () => void; children: ReactNode }) {
  return (
    <main className="game-shell">
      <header className="game-header">
        <div className="brand compact"><span className="brand-mark">2</span><span><strong>TABLE</strong><small>FOR TWO</small></span></div>
        <div className="restaurant-title"><span>{view.config.name}</span><small>Round {view.round}</small></div>
        <div className="game-header-actions">
          <div className={`connection ${connection}`}><span />{connection === "online" ? "Live" : connection === "connecting" ? "Connecting" : "Offline"}</div>
          <button className="leave-button" onClick={onLeave}>Leave</button>
        </div>
      </header>
      <div className="game-subbar">
        <span>Restaurant code</span><strong>{view.code}</strong><span className="role-pill">{roleLabel(view.you.role)}</span>
      </div>
      {notice && <Notice message={notice} onDismiss={onDismissNotice} />}
      <div className="game-content">{children}</div>
    </main>
  );
}

function GameStage({ view, onAction }: { view: RoomView; onAction: (action: GameAction) => void }) {
  if (view.phase === "lobby") return <Lobby view={view} onAction={onAction} />;
  if (view.phase === "customer-order") {
    return view.you.role === "customer"
      ? <OrderStage title="What would you like today?" intro="Choose in secret, then tell the waiter aloud." menuIds={view.config.menuItemIds} buttonLabel="Lock my order" onSubmit={(order) => onAction({ type: "lock_customer_order", payload: { order } })} />
      : <WaitingStage icon="📝" eyebrow="WAITER" title="Get your notepad ready" text="The customer is choosing. Ask what they would like when they say they are ready." />;
  }
  if (view.phase === "waiter-order") {
    return view.you.role === "waiterChef"
      ? <OrderStage title="Take the customer’s order" intro="Listen carefully. Add every dish and ask about the extras." menuIds={view.config.menuItemIds} buttonLabel="Send ticket to kitchen" waiter onSubmit={(order) => onAction({ type: "submit_waiter_order", payload: { order } })} />
      : <WaitingStage icon="🤫" eyebrow="CUSTOMER" title="Tell the waiter your order" text="Keep your screen private. Say each dish and extra aloud, then wait for the kitchen." summary={view.customerOrder} />;
  }
  if (view.phase === "kitchen") {
    return view.you.role === "waiterChef" && view.waiterTicket
      ? <Kitchen view={view} ticket={view.waiterTicket} onServe={(tray) => onAction({ type: "serve_tray", payload: { tray } })} />
      : <WaitingStage icon="🍳" eyebrow="CUSTOMER" title="Your meal is cooking" text="The waiter has become the chef. Get ready to check the tray when it arrives." />;
  }
  if (view.phase === "review") return <Review view={view} onAction={onAction} />;
  if (view.phase === "bill") return <BillStage view={view} onPay={() => onAction({ type: "pay_bill", payload: {} })} />;
  return <PaidStage view={view} onNext={() => onAction({ type: "new_round", payload: {} })} />;
}

function Lobby({ view, onAction }: { view: RoomView; onAction: (action: GameAction) => void }) {
  const other = view.participants.find((participant) => participant.seat !== view.you.seat);
  return (
    <section className="stage lobby-stage">
      <div className="stage-heading"><p className="eyebrow">THE RESTAURANT IS OPEN</p><h1>Choose your first role</h1><p>There can be one customer and one waiter. The waiter becomes the chef later.</p></div>
      <div className="role-grid">
        <RoleCard role="customer" emoji="🙋" title="Customer" description="Choose a secret meal, explain it clearly, then check what arrives." selected={view.you.role === "customer"} unavailable={other?.role === "customer"} onSelect={() => onAction({ type: "set_role", payload: { role: "customer" } })} />
        <RoleCard role="waiterChef" emoji="🧑‍🍳" title="Waiter → Chef" description="Listen, write the ticket, then head to the kitchen and build the tray." selected={view.you.role === "waiterChef"} unavailable={other?.role === "waiterChef"} onSelect={() => onAction({ type: "set_role", payload: { role: "waiterChef" } })} />
      </div>
      <div className="lobby-status">
        <span className={view.participants.length === 2 ? "ready-dot" : "waiting-dot"} />
        {view.participants.length < 2 ? <>Waiting for player two — share code <strong>{view.code}</strong></> : view.participants.every((participant) => participant.role) ? "Everyone is ready!" : "Both players are here. Choose different roles."}
      </div>
    </section>
  );
}

function RoleCard({ emoji, title, description, selected, unavailable, onSelect }: { role: Role; emoji: string; title: string; description: string; selected: boolean; unavailable: boolean; onSelect: () => void }) {
  return (
    <button className={`role-card ${selected ? "selected" : ""}`} disabled={unavailable} onClick={onSelect}>
      <span className="role-emoji">{emoji}</span><span className="role-copy"><strong>{title}</strong><small>{description}</small></span>
      <span className="role-action">{selected ? "✓ Chosen" : unavailable ? "Taken" : "Choose role"}</span>
    </button>
  );
}

function OrderStage({ title, intro, menuIds, buttonLabel, waiter = false, onSubmit }: { title: string; intro: string; menuIds: string[]; buttonLabel: string; waiter?: boolean; onSubmit: (order: OrderSelection) => void }) {
  const [order, setOrder] = useState<OrderSelection>({ lines: [] });
  const menu = MENU_ITEMS.filter((item) => menuIds.includes(item.id));
  const hasMain = order.lines.some((line) => getMenuItem(line.itemId)?.category === "main");
  return (
    <section className="stage order-stage">
      <div className="order-stage-heading">
        <div><p className="eyebrow">{waiter ? "WAITER'S NOTEPAD" : "CUSTOMER'S PRIVATE MENU"}</p><h1>{title}</h1><p>{intro}</p></div>
        <div className="privacy-stamp">{waiter ? "✎ Listen carefully" : "🔒 Only you can see this"}</div>
      </div>
      <OrderBuilder menu={menu} order={order} onChange={setOrder} />
      <div className="order-footer">
        <div><span>Order total</span><strong>{formatMoney(buildBill(order).totalPence)}</strong></div>
        <button className="button button-primary button-large" disabled={!hasMain} onClick={() => onSubmit(order)}>{buttonLabel} →</button>
      </div>
    </section>
  );
}

function OrderBuilder({ menu, order, onChange }: { menu: MenuItem[]; order: OrderSelection; onChange: (order: OrderSelection) => void }) {
  const selectItem = (item: MenuItem) => {
    const withoutCategory = order.lines.filter((line) => getMenuItem(line.itemId)?.category !== item.category);
    const alreadySelected = order.lines.some((line) => line.itemId === item.id);
    onChange({ lines: alreadySelected ? withoutCategory : [...withoutCategory, { itemId: item.id, extraIds: [] }] });
  };
  const toggleExtra = (itemId: string, extraId: string) => {
    onChange({ lines: order.lines.map((line) => line.itemId !== itemId ? line : { ...line, extraIds: line.extraIds.includes(extraId) ? line.extraIds.filter((id) => id !== extraId) : [...line.extraIds, extraId] }) });
  };
  return (
    <div className="order-builder">
      {(["main", "side", "drink", "dessert"] as MenuCategory[]).map((category) => {
        const items = menu.filter((item) => item.category === category);
        if (!items.length) return null;
        return (
          <section className="order-category" key={category}>
            <div className="order-category-title"><span>{CATEGORY_ICONS[category]}</span><h2>{CATEGORY_LABELS[category]}</h2><small>{category === "main" ? "Choose one" : "Optional · choose one"}</small></div>
            <div className="order-menu-grid">
              {items.map((item) => {
                const line = order.lines.find((entry) => entry.itemId === item.id);
                return (
                  <article className={`order-menu-card ${line ? "selected" : ""}`} key={item.id}>
                    <button className="order-item-button" onClick={() => selectItem(item)} aria-pressed={Boolean(line)}>
                      <span className="order-item-emoji">{item.emoji}</span><span className="order-item-copy"><strong>{item.name}</strong><small>{item.description}</small></span><span className="order-item-price">{formatMoney(item.pricePence)}</span><span className="round-check">{line ? "✓" : "+"}</span>
                    </button>
                    {line && item.allowedExtraIds.length > 0 && (
                      <div className="extras-row"><span>Any extras?</span>{item.allowedExtraIds.map((extraId) => { const extra = getExtra(extraId); if (!extra) return null; const selected = line.extraIds.includes(extraId); return <button key={extraId} className={selected ? "selected" : ""} onClick={() => toggleExtra(item.id, extraId)} aria-pressed={selected}>{extra.emoji} {extra.name}{extra.pricePence ? ` +${formatMoney(extra.pricePence)}` : ""}</button>; })}</div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function Kitchen({ view, ticket, onServe }: { view: RoomView; ticket: OrderSelection; onServe: (tray: ChefTray) => void }) {
  const [components, setComponents] = useState<string[]>([]);
  const menu = MENU_ITEMS.filter((item) => view.config.menuItemIds.includes(item.id));
  const allowedExtraIds = new Set(menu.flatMap((item) => item.allowedExtraIds));
  const extras = EXTRAS.filter((extra) => allowedExtraIds.has(extra.id));
  const add = (id: string) => setComponents((current) => current.length < 20 ? [...current, id] : current);
  const remove = (index: number) => setComponents((current) => current.filter((_, itemIndex) => itemIndex !== index));
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const id = event.dataTransfer.getData("text/plain");
    if (menu.some((item) => item.id === id) || extras.some((extra) => extra.id === id)) add(id);
  };
  return (
    <section className="stage kitchen-stage">
      <div className="kitchen-heading"><div><p className="eyebrow">CHEF'S KITCHEN</p><h1>Build the serving tray</h1><p>Drag food onto the tray, or tap an ingredient to add it.</p></div><span className="order-up-stamp">ORDER<br />UP!</span></div>
      <div className="kitchen-layout">
        <aside className="ticket-board"><div className="ticket-pin" /><p>TABLE 2 · ROUND {view.round}</p><h2>Order ticket</h2><OrderSummary order={ticket} /><div className="ticket-tear" /></aside>
        <div className="pantry">
          <h2>Kitchen counter</h2>
          <p>Tap more than once if two dishes need the same extra.</p>
          <div className="pantry-grid">
            {menu.map((item) => <KitchenItem key={item.id} id={item.id} emoji={item.emoji} name={item.name} onAdd={add} />)}
            {extras.map((extra) => <KitchenItem key={extra.id} id={extra.id} emoji={extra.emoji} name={extra.name} onAdd={add} />)}
          </div>
        </div>
        <div className="tray-area">
          <h2>Serving tray <span>{components.length} items</span></h2>
          <div className={`serving-tray ${components.length ? "filled" : ""}`} onDragOver={(event) => event.preventDefault()} onDrop={onDrop}>
            {!components.length && <div className="empty-tray"><span>↘</span><strong>Drop food here</strong><small>or tap items on the counter</small></div>}
            {components.map((id, index) => { const item = componentLabel(id); return <button className="tray-item" key={`${id}-${index}`} onClick={() => remove(index)} aria-label={`Remove ${item.name}`}><span>{item.emoji}</span><small>{item.name}</small><b>×</b></button>; })}
          </div>
          <button className="button button-primary button-large full-width" disabled={!components.length} onClick={() => onServe({ componentIds: components })}>Serve to customer →</button>
        </div>
      </div>
    </section>
  );
}

function KitchenItem({ id, emoji, name, onAdd }: { id: string; emoji: string; name: string; onAdd: (id: string) => void }) {
  return <button className="kitchen-item" draggable onDragStart={(event) => { event.dataTransfer.setData("text/plain", id); event.dataTransfer.effectAllowed = "copy"; }} onClick={() => onAdd(id)}><span>{emoji}</span><small>{name}</small><b>+</b></button>;
}

function Review({ view, onAction }: { view: RoomView; onAction: (action: GameAction) => void }) {
  const comparison = view.comparison;
  if (!comparison || !view.customerOrder || !view.waiterTicket || !view.chefTray) return <WaitingStage icon="🔍" eyebrow="CHECKING" title="Checking the order" text="The review is being prepared." />;
  const allExact = comparison.waiter.exact && comparison.kitchen.exact;
  const customer = view.you.role === "customer";
  return (
    <section className="stage review-stage">
      <div className="review-heading"><p className="eyebrow">CUSTOMER CHECK</p><h1>{allExact ? "Perfectly served!" : "How did the team do?"}</h1><p>{customer ? "Compare each step, then decide what happens next." : "The customer is checking the ticket and tray."}</p></div>
      <div className="comparison-grid">
        <ComparisonCard number="1" title="Waiter check" subtitle="Wanted → written" comparison={comparison.waiter} expected={view.customerOrder} actual={view.waiterTicket} />
        <ComparisonCard number="2" title="Kitchen check" subtitle="Written → served" comparison={comparison.kitchen} expected={view.waiterTicket} actual={{ lines: [] }} tray={view.chefTray} />
      </div>
      {customer ? (
        <div className="review-actions">
          {allExact ? <button className="button button-primary button-large" onClick={() => onAction({ type: "accept_order", payload: {} })}>Everything looks right — bring the bill →</button> : (
            <>
              {!comparison.waiter.exact && <button className="button button-coral button-large" onClick={() => onAction({ type: "fix_waiter", payload: {} })}>Ask waiter to fix ticket</button>}
              {comparison.waiter.exact && !comparison.kitchen.exact && <button className="button button-coral button-large" onClick={() => onAction({ type: "fix_kitchen", payload: {} })}>Send tray back to kitchen</button>}
            </>
          )}
        </div>
      ) : <div className="waiting-strip"><span className="waiting-dot" /> Waiting for the customer’s decision</div>}
    </section>
  );
}

function ComparisonCard({ number, title, subtitle, comparison, expected, actual, tray }: { number: string; title: string; subtitle: string; comparison: { missing: string[]; extra: string[]; exact: boolean }; expected: OrderSelection; actual: OrderSelection; tray?: ChefTray }) {
  return (
    <article className={`comparison-card ${comparison.exact ? "correct" : "needs-fix"}`}>
      <header><span>{number}</span><div><h2>{title}</h2><small>{subtitle}</small></div><b>{comparison.exact ? "✓ MATCH" : "! CHECK"}</b></header>
      <div className="compare-columns"><div><h3>Expected</h3><OrderSummary order={expected} compact /></div><div><h3>{tray ? "On tray" : "Recorded"}</h3>{tray ? <ComponentList ids={tray.componentIds} /> : <OrderSummary order={actual} compact />}</div></div>
      {!comparison.exact && <div className="difference-box">{comparison.missing.length > 0 && <p><strong>Missing:</strong> {comparison.missing.map((id) => componentLabel(id).name).join(", ")}</p>}{comparison.extra.length > 0 && <p><strong>Extra:</strong> {comparison.extra.map((id) => componentLabel(id).name).join(", ")}</p>}</div>}
    </article>
  );
}

function BillStage({ view, onPay }: { view: RoomView; onPay: () => void }) {
  const bill = view.bill;
  if (!bill) return <WaitingStage icon="🧾" eyebrow="THE BILL" title="Preparing the bill" text="It will be ready in a moment." />;
  const customer = view.you.role === "customer";
  return (
    <section className="stage bill-stage">
      <div className="bill-copy"><p className="eyebrow">MEAL COMPLETE</p><h1>A brilliant service!</h1><p>The order matched from the customer’s first choice all the way to the tray.</p><div className="celebration-dots">● <span>●</span> ● <span>●</span></div></div>
      <article className="receipt"><div className="receipt-top"><span className="brand-mark mini">2</span><h2>{view.config.name}</h2><p>TABLE 2 · ROUND {view.round}</p></div><div className="receipt-lines">{bill.lines.map((line, index) => <div key={`${line.label}-${index}`}><span>{line.label}</span><strong>{formatMoney(line.pricePence)}</strong></div>)}</div><div className="receipt-total"><span>TOTAL</span><strong>{formatMoney(bill.totalPence)}</strong></div>{customer ? <button className="button button-primary button-large full-width" onClick={onPay}>Pay bill ✓</button> : <div className="waiting-strip"><span className="waiting-dot" /> Customer is paying</div>}<div className="ticket-tear" /></article>
    </section>
  );
}

function PaidStage({ view, onNext }: { view: RoomView; onNext: () => void }) {
  return (
    <section className="stage paid-stage"><div className="paid-burst"><span>★</span><span>✓</span><span>★</span></div><p className="eyebrow">THANK YOU!</p><h1>Bill paid</h1><p>Round {view.round} is complete. Ready to welcome another customer?</p><button className="button button-primary button-large" onClick={onNext}>Start another order →</button></section>
  );
}

function WaitingStage({ icon, eyebrow, title, text, summary }: { icon: string; eyebrow: string; title: string; text: string; summary?: OrderSelection }) {
  return (
    <section className="stage waiting-stage"><div className="waiting-icon">{icon}</div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{text}</p>{summary && <div className="private-summary"><span>🔒 Your private order</span><OrderSummary order={summary} /></div>}<div className="animated-wait"><i /><i /><i /></div></section>
  );
}

function OrderSummary({ order, compact = false }: { order: OrderSelection; compact?: boolean }) {
  return <ul className={`order-summary ${compact ? "compact" : ""}`}>{order.lines.map((line) => { const item = getMenuItem(line.itemId); if (!item) return null; return <li key={line.itemId}><span>{item.emoji}</span><div><strong>{item.name}</strong>{line.extraIds.length > 0 && <small>{line.extraIds.map((id) => getExtra(id)?.name).filter(Boolean).join(" · ")}</small>}</div></li>; })}</ul>;
}

function ComponentList({ ids }: { ids: string[] }) {
  return <ul className="component-list">{ids.map((id, index) => { const item = componentLabel(id); return <li key={`${id}-${index}`}><span>{item.emoji}</span>{item.name}</li>; })}</ul>;
}

function Notice({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return <div className="notice" role="alert"><span>!</span><p>{message}</p><button onClick={onDismiss} aria-label="Dismiss message">×</button></div>;
}

function LoadingScreen() {
  return <main className="loading-screen"><div className="brand-mark">2</div><h1>Setting the table…</h1></main>;
}

function roleLabel(role: Role | null): string {
  if (role === "customer") return "🙋 Customer";
  if (role === "waiterChef") return "🧑‍🍳 Waiter / Chef";
  return "Choose a role";
}

async function readApiPayload(response: Response): Promise<{ code: string; view: RoomView }> {
  const value: unknown = await response.json();
  if (!response.ok) {
    const message = isRecord(value) && typeof value.error === "string" ? value.error : "The restaurant could not be reached.";
    throw new Error(message);
  }
  if (!isRecord(value) || typeof value.code !== "string" || !isRoomView(value.view)) throw new Error("The restaurant sent an unexpected response.");
  return { code: value.code, view: value.view };
}

function parseServerMessage(value: unknown): ServerMessage | null {
  if (typeof value !== "string") return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!isRecord(parsed) || (parsed.type !== "state" && parsed.type !== "error")) return null;
    if (parsed.type === "state" && isRoomView(parsed.view)) return { type: "state", view: parsed.view };
    if (parsed.type === "error" && typeof parsed.message === "string") {
      return { type: "error", message: parsed.message, view: isRoomView(parsed.view) ? parsed.view : undefined };
    }
    return null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isRoomView(value: unknown): value is RoomView {
  if (!isRecord(value) || typeof value.code !== "string" || typeof value.version !== "number" || typeof value.round !== "number") return false;
  if (typeof value.phase !== "string" || !["lobby", "customer-order", "waiter-order", "kitchen", "review", "bill", "paid"].includes(value.phase)) return false;
  if (!isRecord(value.config) || typeof value.config.name !== "string" || !Array.isArray(value.config.menuItemIds)) return false;
  if (!isRecord(value.you) || (value.you.seat !== "host" && value.you.seat !== "guest")) return false;
  return Array.isArray(value.participants) && typeof value.expiresAt === "number";
}
